import {
    ButtonGradientPrimary,
    ButtonSecondary,
    MultilineInput,
    TextInput,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
    CompetencyGroupFormFields,
    competencyGroupSchema,
    isNameConflictError,
    NAME_MAX_LENGTH,
    DESCRIPTION_MAX_LENGTH,
    type CompetencyGroupFormData,
} from '../validation';
import { CharacterCount } from './profiles/CharacterCount';

import type { TranslationKey } from '@/i18n-key';

import { useAmelioTranslations } from '@/hooks';
import { validationMessageKey } from '@/utils/validationMessage';

const NAME_DUPLICATE_MESSAGE: TranslationKey = 'competencies.validation.groupNameDuplicate';

type CompetencyGroupFormProps = {
    mode: 'create' | 'edit';
    defaultValues: CompetencyGroupFormData;
    isSubmitting: boolean;
    onSubmit: (data: CompetencyGroupFormData) => void;
    onCancel: () => void;
};

export const CompetencyGroupForm = ({
    mode,
    defaultValues,
    isSubmitting,
    onSubmit,
    onCancel,
}: CompetencyGroupFormProps) => {
    const { t } = useAmelioTranslations();

    const [nameConflict, setNameConflict] = useState(false);

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<CompetencyGroupFormData>({
        resolver: zodResolver(competencyGroupSchema),
        defaultValues,
    });

    const handleValidSubmit = async (data: CompetencyGroupFormData) => {
        setNameConflict(false);

        try {
            await onSubmit(data);
        } catch (error) {
            if (isNameConflictError(error)) {
                setNameConflict(true);

                return;
            }

            throw error;
        }
    };

    const nameErrorMessage = errors.name?.message
        ? t(validationMessageKey(errors.name.message))
        : nameConflict
          ? t(NAME_DUPLICATE_MESSAGE)
          : undefined;

    return (
        <form
            onSubmit={handleSubmit(handleValidSubmit)}
            className={'flex flex-col gap-4 p-1'}
        >
            <div className={'mb-4'}>
                <h4 className={'text-navy mb-1 text-xl font-medium'}>
                    {t(
                        mode === 'edit'
                            ? 'competencies.groupForm.editTitle'
                            : 'competencies.groupForm.createTitle',
                    )}
                </h4>
                <p className={'text-sm text-gray-500'}>
                    {t(
                        mode === 'edit'
                            ? 'competencies.groupForm.description'
                            : 'competencies.groupForm.descriptionHelper',
                    )}
                </p>
            </div>

            <Controller
                control={control}
                name={CompetencyGroupFormFields.name}
                render={({ field }) => (
                    <div className={'flex flex-col gap-2'}>
                        <label className={'text-navy text-sm font-medium'}>
                            {t('competencies.groupForm.namePlaceholder')}

                            <span className={'text-error ml-0.5'}>*</span>
                        </label>

                        <TextInput
                            value={field.value}
                            onChange={(value) => {
                                setNameConflict(false);
                                field.onChange(value);
                            }}
                            placeholder={t('competencies.groupForm.namePlaceholder')}
                            size={'md'}
                            radius={'md'}
                            className={
                                'focus-within:ring-primary border border-gray-200 bg-white text-sm focus-within:border-gray-200 focus-within:ring-2 focus-within:ring-offset-2'
                            }
                            maxLength={NAME_MAX_LENGTH}
                            dataTestId={'competency-group-name-input'}
                        />
                        <CharacterCount
                            value={field.value}
                            maxLength={NAME_MAX_LENGTH}
                        />

                        {nameErrorMessage && (
                            <span className={'text-error text-xs'}>{nameErrorMessage}</span>
                        )}
                    </div>
                )}
            />

            <Controller
                control={control}
                name={CompetencyGroupFormFields.description}
                render={({ field }) => (
                    <div className={'flex flex-col gap-2'}>
                        <label className={'text-navy text-sm font-medium'}>
                            {t('competencies.groupForm.descriptionLabel')}
                        </label>

                        <MultilineInput
                            value={field.value}
                            onChange={(value) => field.onChange(value)}
                            placeholder={t('competencies.groupForm.descriptionPlaceholder')}
                            className={
                                'focus-within:ring-primary/20 border border-gray-200 bg-white text-sm focus-within:ring-2'
                            }
                            rows={3}
                            autoGrow={true}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                            dataTestId={'competency-group-description-input'}
                        />
                        <CharacterCount
                            value={field.value}
                            maxLength={DESCRIPTION_MAX_LENGTH}
                        />

                        {errors.description?.message && (
                            <span className={'text-error text-xs'}>
                                {t(validationMessageKey(errors.description.message))}
                            </span>
                        )}
                    </div>
                )}
            />

            <div className={'mt-2 flex w-full items-center !justify-end gap-3'}>
                <ButtonSecondary
                    type={'button'}
                    size={'md'}
                    onClick={onCancel}
                    dataTestId={'competency-group-cancel'}
                    fitContent={true}
                    className={'text-sm'}
                >
                    {t('common.cancel')}
                </ButtonSecondary>

                <ButtonGradientPrimary
                    type={'submit'}
                    size={'md'}
                    disabled={isSubmitting}
                    dataTestId={'competency-group-submit'}
                    fitContent={true}
                    className={'text-sm'}
                >
                    {t('common.save')}
                </ButtonGradientPrimary>
            </div>
        </form>
    );
};
